// Pack purchase with optional Shopify/Appstle selling plans. Variant prices remain
// full-pack prices and quantity is ALWAYS 1. No client discounts, cart clearing,
// or automatic retry of cart POSTs.
export function variantFor(pack, flavor) {
  return pack?.variants.find((variant) => variant.flavor === flavor) ?? null;
}

export function planForVariant(variant, id) {
  if (!id) return null;
  return variant?.plans?.find((plan) => plan.id === String(id)) ?? null;
}

// Some Appstle plan option values include internal rule data instead of a
// customer-facing label. Keep the original selling-plan ID, but turn the
// recognizable delivery interval into the compact design label.
export function displayPlanLabel(plan) {
  const sources = [plan?.label, plan?.name].filter(Boolean).map((value) => String(value).trim());
  for (const source of sources) {
    const interval = source.match(/(\d+)\s*(day|days|week|weeks|month|months|monthly|year|years)/i);
    if (!interval) continue;
    const count = Number(interval[1]);
    const unit = interval[2].toLowerCase();
    if (unit.startsWith('month')) return `${count * 30} DAYS`;
    if (unit.startsWith('week')) return `${count * 7} DAYS`;
    if (unit.startsWith('day')) return `${count} ${count === 1 ? 'DAY' : 'DAYS'}`;
    return `${count} ${count === 1 ? 'YEAR' : 'YEARS'}`;
  }
  const fallback = sources.find((value) => !/(MIN|MAX)_CYCLES|PERCENTAGE|NULL/i.test(value));
  return (fallback || 'SUBSCRIPTION').replace(/\s+subscription\s*$/i, '').trim().toUpperCase();
}

export function subscriptionCopy(template, discount) {
  const value = Number.isFinite(Number(discount)) ? String(discount) : '—';
  return String(template || '')
    .replaceAll('[percent]', value)
    .replace(/\[\s*\d+(?:\.\d+)?\s*\](?=\s*%)/g, value);
}

export function selectionForId(packs, id) {
  if (!id) return null;
  for (const pack of packs) {
    const variant = pack.variants.find((item) => item.id === String(id));
    if (variant) return { pack, variant, flavor: variant.flavor };
  }
  return null;
}

export function initialSelection(packs, config) {
  const initial = selectionForId(packs, config.initialVariantId);

  // The card checkbox always wins for the initial display.
  // This also keeps a sold-out default card visibly selected.
  const checkedPack = packs.find((pack) => pack.defaultSelected === true);
  if (checkedPack) {
    const variant = variantFor(checkedPack, initial?.flavor) || checkedPack.variants[0];
    if (variant) {
      return { pack: checkedPack, variant, flavor: variant.flavor };
    }
  }

  // Existing behavior when no supply-card checkbox is enabled.
  const linked = selectionForId(packs, config.explicitVariantId);
  if (linked) return linked;

  const preferred = packs.find((pack) => pack.bags === config.defaultBags);
  if (preferred) {
    const variant = variantFor(preferred, initial?.flavor) || preferred.variants[0];
    if (variant) {
      return { pack: preferred, variant, flavor: variant.flavor };
    }
  }

  if (initial) return initial;

  for (const pack of packs) {
    const variant = pack.variants[0];
    if (variant) return { pack, variant, flavor: variant.flavor };
  }

  return null;
}

export function validatePacks(packs) {
  const values = packs.map((pack) => pack.value.trim().toLowerCase());
  return packs.length === 3 && new Set(values).size === 3 && values.every(Boolean) && packs.every((pack) =>
    Number.isInteger(pack.bags) && pack.bags > 0 && Number.isInteger(pack.servings) && pack.servings > 0 &&
    Array.isArray(pack.variants) && new Set(pack.variants.map((v) => v.flavor)).size === pack.variants.length &&
    pack.variants.every((variant) => Array.isArray(variant.plans) &&
      new Set(variant.plans.map((plan) => plan.id)).size === variant.plans.length)
  );
}

export class AthleanPackPicker extends HTMLElement {
  connectedCallback() {
    this.listeners?.abort();
    this.mediaSelectionKey = undefined;
    this.listeners = new AbortController();
    const { signal } = this.listeners;
    try {
      this.config = JSON.parse(this.querySelector('[data-config]').getAttribute('data-config'));
      this.cards = [...this.querySelectorAll('[data-pack]')];
      this.packs = this.cards.map((card) => JSON.parse(card.querySelector('[data-pack-config]').getAttribute('data-pack-config')));
      this.form = this.querySelector('form');
      this.button = this.querySelector('[data-submit]');
      if (!this.form || !this.button) return;
      if (!this.config.valid || !validatePacks(this.packs)) {
        this.showError(this.config.labels.setup);
        return;
      }
      this.selection = initialSelection(this.packs, this.config);
      if (!this.selection) {
        this.showError(this.config.labels.setup);
        return;
      }
      this.subscriptionEnabled = Boolean(this.config.showSubscription && this.config.hasSellingPlans &&
        (this.config.requiresSellingPlan || this.config.explicitSellingPlanId || this.config.subscriptionDefault));
      this.selectedPlanId = this.config.explicitSellingPlanId || '';
      this.addEventListener('change', (event) => this.changeSelection(event), { signal });
      this.form.addEventListener('submit', (event) => this.submitPack(event), { signal });
      window.addEventListener('popstate', () => {
        if (this.busy) return;
        const linked = selectionForId(this.packs, new URL(location.href).searchParams.get('variant'));
        this.selection = linked || initialSelection(this.packs, { ...this.config, explicitVariantId: '' });
        this.selectedPlanId = new URL(location.href).searchParams.get('selling_plan') || '';
        this.subscriptionEnabled = Boolean(this.config.requiresSellingPlan || this.selectedPlanId);
        this.update();
      }, { signal });
      this.update();
    } catch (error) {
      console.warn('[athlean-purchase] Invalid purchase configuration.', error);
      if (this.config) this.showError(this.config.labels.setup);
    }
  }

  disconnectedCallback() {
    this.listeners?.abort();
    cancelAnimationFrame(this.mediaFrame);
  }

  showError(message) {
    const element = this.querySelector('[data-error]');
    if (element) {
      element.textContent = message || '';
      element.hidden = !message;
    }
  }

  setText(selector, text, scope = this) {
    const element = scope.querySelector(selector);
    if (element) element.textContent = text ?? '';
  }

  changeSelection(event) {
    if (this.busy || !this.selection) return;
    if (event.target.matches('[data-subscription-toggle]')) {
      if (this.config.requiresSellingPlan) return;
      this.subscriptionEnabled = event.target.checked;
    } else if (event.target.matches('[data-selling-plan-select]')) {
      this.selectedPlanId = event.target.value;
      this.subscriptionEnabled = true;
    } else if (event.target.matches('[data-flavor]')) {
      this.selection.flavor = event.target.value;
      this.selection.variant = variantFor(this.selection.pack, this.selection.flavor);
    } else if (event.target.matches('[data-supply]')) {
      this.selection.pack = this.packs.find((pack) => pack.value === event.target.value);
      this.selection.variant = variantFor(this.selection.pack, this.selection.flavor);
    } else return;
    this.update();
    this.syncUrl();
  }

  syncUrl() {
    // Preserve other query parameters; do not remorph or replace the left gallery.
    if (this.config.productId && !window.Shopify?.designMode) {
      const url = new URL(location.href);
      url.searchParams.delete('option_values');
      if (this.selection.variant) url.searchParams.set('variant', this.selection.variant.id);
      else url.searchParams.delete('variant');
      const plan = this.subscriptionEnabled ? planForVariant(this.selection.variant, this.selectedPlanId) : null;
      if (plan) url.searchParams.set('selling_plan', plan.id);
      else url.searchParams.delete('selling_plan');
      history.replaceState(history.state, '', url);
    }
  }

  syncVariantMedia() {
    const variant = this.selection?.variant;
    const key = variant?.id || '';
    // Price, subscription and cart updates must not reset a manually browsed gallery.
    if (key === this.mediaSelectionKey) return;
    this.mediaSelectionKey = key;
    cancelAnimationFrame(this.mediaFrame);
    if (!variant?.featuredMediaId) return;
    const signal = this.listeners.signal;
    const showMedia = () => {
      if (signal.aborted || !this.isConnected || this.mediaSelectionKey !== key) return;
      const section = this.closest('.shopify-section');
      const gallery = [...(section?.querySelectorAll('media-gallery[data-athlean-product-id]') || [])]
        .find((element) => element.dataset.athleanProductId === String(this.config.productId));
      if (!gallery) return;
      const mediaId = String(variant.featuredMediaId);
      const slideshow = gallery.querySelector('slideshow-component');
      const slides = [...(slideshow?.querySelectorAll('slideshow-slide') || [])];
      const index = slides.findIndex((slide) => slide.dataset.athleanMediaId === mediaId);
      if (index >= 0 && typeof slideshow.select === 'function') {
        // Use the theme API so thumbnails, arrows and the mobile counter stay in sync.
        slideshow.select(index, undefined, { animate: false });
      }
      // Keep DOM indexes intact for the theme's zoom handlers in desktop grid mode.
      const grid = gallery.querySelector('.media-gallery__grid');
      if (grid) {
        for (const item of grid.children) {
          item.style.order = item.dataset.athleanMediaId === mediaId ? '-1' : '';
        }
      }
    };
    customElements.whenDefined('slideshow-component').then(() => {
      if (signal.aborted || this.mediaSelectionKey !== key) return;
      this.mediaFrame = requestAnimationFrame(() => {
        this.mediaFrame = requestAnimationFrame(showMedia);
      });
    });
  }

  updatePlanOptions(plans) {
    const select = this.querySelector('[data-selling-plan-select]');
    if (!select) return;
    const signature = plans.map((plan) => `${plan.id}:${plan.label}`).join('|');
    if (select.dataset.signature === signature) return;
    select.replaceChildren(...plans.map((plan) => {
      const option = document.createElement('option');
      option.value = plan.id;
      option.textContent = displayPlanLabel(plan);
      return option;
    }));
    select.dataset.signature = signature;
  }

  update() {
    if (!this.selection) return;
    this.showError('');
    const { labels } = this.config;
    const { pack: selectedPack, flavor } = this.selection;
    for (const radio of this.querySelectorAll('[data-flavor]')) {
      radio.checked = radio.value === flavor;
      radio.disabled = Boolean(this.busy);
    }
    this.cards.forEach((card, index) => {
      const pack = this.packs[index];
      const variant = variantFor(pack, flavor);
      const radio = card.querySelector('[data-supply]');
      radio.checked = pack === selectedPack;
      radio.disabled = Boolean(this.busy || !variant?.eligible);
      card.dataset.selected = String(radio.checked);
      card.dataset.unavailable = String(!variant?.eligible);
      this.setText('[data-pack-price]', variant?.perBag ?? '—', card);
      this.setText('[data-pack-serving]', variant?.perServing ?? '—', card);
      const compare = card.querySelector('[data-pack-compare]');
      compare.hidden = !(variant?.compare > variant?.price);
      compare.textContent = compare.hidden ? '' : variant.perBagCompare;
      const discount = card.querySelector('[data-discount]');
      if (discount) {
        discount.hidden = !(variant?.discount > 0);
        discount.textContent = discount.hidden ? '' : `(${(pack.discountLabel || '[percent]% OFF').replaceAll('[percent]', variant.discount)})`;
      }
      const unavailable = card.querySelector('[data-pack-unavailable]');
      unavailable.hidden = Boolean(variant?.eligible);
      unavailable.textContent = variant && !variant.available ? labels.soldOut : labels.unavailable;
    });
    const variant = variantFor(selectedPack, flavor);
    this.selection.variant = variant;
    this.syncVariantMedia();
    const plans = variant?.plans ?? [];
    let selectedPlan = planForVariant(variant, this.selectedPlanId);
    if (!selectedPlan && plans.length) {
      selectedPlan = plans[0];
      this.selectedPlanId = selectedPlan.id;
    }
    if (this.config.requiresSellingPlan) this.subscriptionEnabled = true;
    const activePlan = this.subscriptionEnabled ? selectedPlan : null;
    const purchaseEligible = Boolean(variant?.eligible &&
      (this.subscriptionEnabled ? activePlan : !this.config.requiresSellingPlan));

    const subscription = this.querySelector('[data-subscription]');
    if (subscription) {
      subscription.hidden = !this.config.showSubscription || !this.config.hasSellingPlans;
      subscription.dataset.selected = String(Boolean(this.subscriptionEnabled));
      subscription.dataset.unavailable = String(!plans.length);
      const toggle = subscription.querySelector('[data-subscription-toggle]');
      toggle.checked = Boolean(this.subscriptionEnabled);
      toggle.disabled = Boolean(this.busy || this.config.requiresSellingPlan);
      this.updatePlanOptions(plans);
      const select = subscription.querySelector('[data-selling-plan-select]');
      if (selectedPlan) select.value = selectedPlan.id;
      select.disabled = Boolean(this.busy || !this.subscriptionEnabled || !plans.length);
      subscription.querySelector('[data-subscription-controls]').hidden = !this.subscriptionEnabled;
      const subscriptionError = subscription.querySelector('[data-subscription-error]');
      subscriptionError.hidden = !(this.subscriptionEnabled && !selectedPlan);
      const copy = subscription.querySelector('[data-subscription-copy]');
      copy.textContent = subscriptionCopy(copy.dataset.template, selectedPlan?.discount);
    }

    const input = this.querySelector('[data-variant-id]');
    input.value = variant?.id || '';
    input.disabled = !purchaseEligible;
    const planInput = this.querySelector('[data-selling-plan-input]');
    planInput.value = activePlan?.id || '';
    planInput.disabled = !activePlan;
    this.button.disabled = Boolean(this.busy || !purchaseEligible);
    this.button.setAttribute('aria-busy', String(Boolean(this.busy)));
    this.setText('[data-submit-label]', this.busy ? labels.adding : purchaseEligible ? labels.add : variant && !variant.available ? labels.soldOut : labels.unavailable);
    const displayPrice = activePlan?.checkoutChargeTotal || variant?.total || '';
    const displayPriceCents = activePlan?.checkoutCharge ?? variant?.price ?? 0;
    this.setText('[data-total]', displayPrice);
    const compare = this.querySelector('[data-total-compare]');
    if (variant?.compare > displayPriceCents) compare.textContent = variant.totalCompare;
    else if (activePlan?.compareAt > displayPriceCents) compare.textContent = activePlan.compareTotal;
    else compare.textContent = '';
    compare.hidden = !compare.textContent;
    const planStatus = activePlan ? `, ${activePlan.name}` : '';
    this.setText('[data-status]', purchaseEligible ? `${selectedPack.value}, ${flavor}${planStatus}, ${displayPrice}` : labels.unavailable);
  }

  async submitPack(event) {
    event.preventDefault();
    const selectedPlan = this.subscriptionEnabled ? planForVariant(this.selection?.variant, this.selectedPlanId) : null;
    if (this.busy || !this.selection?.variant?.eligible ||
      (this.subscriptionEnabled && !selectedPlan) || (this.config.requiresSellingPlan && !selectedPlan)) return;

    const variant = this.selection.variant;
    const { labels } = this.config;
    const root = window.Shopify?.routes?.root || '/';
    const checkoutUrl = root.replace(/\/$/, '') + '/checkout';
    let redirecting = false;
    this.busy = true;
    this.update();

    try {
      const item = { id: variant.id, quantity: 1 };
      if (selectedPlan) item.selling_plan = selectedPlan.id;

      const response = await fetch(this.config.addUrl, {
        method: 'POST', credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [item] }),
      });
      const result = await response.json();
      if (!response.ok || result.status) {
        const description = typeof result.description === 'string' ? result.description : result.message;
        const cartError = new Error(description || labels.error);
        cartError.isCartResponseError = true;
        throw cartError;
      }

      // Add succeeded: go directly to checkout with the current cart.
      this.setText('[data-status]', labels.added);
      window.location.assign(checkoutUrl);
      redirecting = true;
    } catch (error) {
      this.showError(error?.isCartResponseError ? error.message : labels.error);
      this.setText('[data-status]', labels.error);
    } finally {
      // Keep the button disabled during navigation to prevent duplicate adds.
      if (!redirecting) {
        this.busy = false;
        const message = this.querySelector('[data-error]')?.textContent;
        const failed = !this.querySelector('[data-error]')?.hidden;
        this.update();
        if (failed) this.showError(message);
      }
    }
  }
}

if (!customElements.get('athlean-pack-picker')) customElements.define('athlean-pack-picker', AthleanPackPicker);
